import React, { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Haptics from '../utils/haptics';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen, onClose, onConfirm, title, message, confirmText, cancelText
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useApp();

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsLoading(true);
    Haptics.success();
    try {
      await onConfirm();
      // Auto-close on success to prevent the modal from getting "stuck" in global state
      onClose(); 
    } catch (error) {
      console.error("Confirmation action failed:", error);
      // Stay open on error so the user can see if something went wrong
    } finally {
      setIsLoading(false); 
    }
  };
  
  const handleCancel = () => {
    Haptics.warning();
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[101] bg-black/60 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl shadow-2xl p-6 text-center">
        <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
          <AlertTriangle size={24} className="text-red-500" />
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{title}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={handleCancel} disabled={isLoading} className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50">
            {cancelText || t('cancel')}
          </button>
          <button onClick={handleConfirm} disabled={isLoading} className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-500/20 disabled:opacity-70 flex items-center justify-center gap-2">
            {isLoading && <Loader2 size={16} className="animate-spin" />}
            {isLoading ? t('saving') : (confirmText || t('delete'))}
          </button>
        </div>
      </div>
    </div>
  );
};
